import React, { useState, useEffect, useCallback, useContext } from 'react';
import styles from './PassKeyAuthModal.module.css';
import axios from "../../../../utils/api/axios";
import { DialogEnum, showDialog } from '../../../../utils/dialog/Dialog';
import { IAM_MFA_OTP, IAM_SERVER_MULTIFACTORPOLICY, IAM_SERVER_UNIFIEDPROFILE } from '../../../../utils/Config';
import { Fido2 } from '../../../../core/fido2.ts';
import TimeAgo from '../../../../utils/date/timeUtils';
import { getCookie } from '../../../../utils/cookies/cookies';
import { InnocodeButton } from '@intsoft/innocode-core';

const getDeviceIcon = (type) => {
  // FIDO2 credential의 aaguid 등을 기반으로 아이콘을 결정할 수 있습니다.
  // 여기서는 간단히 기본 아이콘을 반환합니다.
  if (type && type.toLowerCase().includes('windows')) {
    return '💻'; // Windows Hello
  }
  if (type && type.toLowerCase().includes('android')) {
    return '📱'; // Android
  }
  return '🔑'; // 일반 보안 키
};

/**
 * 클라이언트 기기 정보를 분석하여 FIDO2 authenticatorAttachment 타입을 결정합니다.
 * @returns {'platform' | 'cross-platform' | undefined}
 */
const getAuthenticatorAttachment = () => {
  // 1. 최신 API (navigator.userAgentData) 사용
  if (navigator.userAgentData) {
    if (navigator.userAgentData.mobile) {
      return 'platform'; // 모바일 기기는 'platform'을 우선적으로 제안
    }
    // 데스크톱의 경우, 사용자가 선택하도록 undefined를 반환하는 것이 가장 좋은 UX
    return undefined;
  }

  // 2. userAgentData 미지원 시 userAgent로 폴백
  const ua = navigator.userAgent.toLowerCase();
  if (/android|iphone|ipad|ipod|webos|blackberry|iemobile|opera mini/i.test(ua)) {
    return 'cross-platform';
  }

  return undefined; // 데스크톱은 선택권을 줌
};

function PassKeyAuthModal({ onClose, onSuccess }) {
  const [step, setStep] = useState("verify");
  const [currentPw, setCurrentPw] = useState("");
  const [userId] = useState(getCookie("user_id"));

  // 비밀번호 표시 상태 추가
  const [showPassword, setShowPassword] = useState(false);

  // 등록된 패스키 목록을 저장하는 상태
  const [credentials, setCredentials] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPassword, setIsPassword] = useState(false);

  // --- 1. 데스크톱 환경 여부를 저장할 상태 추가 ---
  const [isDesktop, setIsDesktop] = useState(false);

  // --- 2. 컴포넌트 마운트 시 기기 환경을 한 번만 체크 ---
  useEffect(() => {
    // navigator.userAgentData가 더 최신 방식이지만, 간결성을 위해 userAgent 사용 예시
    const ua = navigator.userAgent.toLowerCase();
    const desktopCheck = !/android|iphone|ipad|ipod|webos|blackberry|iemobile|opera mini/i.test(ua);
    setIsDesktop(desktopCheck);
  }, []); // 빈 배열로 마운트 시 한 번만 실행

  // 초기 OTP 등록 상태를 확인합니다.
  const checkPolicy = async () => {
    const conditions = [
      { field: "unifiedProfileId", operator: "EQUALS", value: userId },
    ];
    const params = {
      page: 0,
      size: 0,
      sortField: ["createdDate"],
      sortOrder: ["desc"],
      operator: "AND",
      conditions
    };
    try {
      const res = await axios.post(`${IAM_SERVER_MULTIFACTORPOLICY}/all`, params);

      const fidoPolicy = res.data.data.data.find(p => p.factorType === 'FIDO2');

      if (fidoPolicy !== null) {
        setPolicy(fidoPolicy);
      }

    } catch (err) {
    }
  };

  const fetchCredentials = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await Fido2.findUserNameCredentials(userId);
      const resultJson = JSON.parse(result);
      setCredentials(resultJson.credentials);
    } catch (error) {
      showDialog(DialogEnum.DEFAULT, "오류", "패스키 목록을 불러오는 데 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    // 비밀번호 확인 단계가 아니라면, 즉시 목록을 불러옴 (비밀번호 확인을 생략하고 싶을 경우)
    // 여기서는 비밀번호 확인 후 목록을 불러오는 흐름을 유지
    if (step === "manage") {
      fetchCredentials();
      checkPolicy();
    }
  }, [step, fetchCredentials]);

  // 비밀번호 검증
  const verifyPassword = async () => {
    try {
      const res = await axios.post(`${IAM_SERVER_UNIFIEDPROFILE}/verify/password`, {
        password: currentPw,
        unifiedProfileId: userId
      });

      const data = res.data;
      if (data.code === 200 || data.success === true) {
        setStep("manage");
        setIsPassword(true);
      } else {
        showDialog(DialogEnum.DEFAULT, "실패", data.resultMessage || "현재 비밀번호가 일치하지 않습니다.");
      }
    } catch (err) {
      showDialog(DialogEnum.DEFAULT, "오류", "서버 오류가 발생했습니다.");
    }
  };

  const Fido2Registration = async (attachmentType) => {
    try {
      setIsLoading(true);
      console.log("Fido2Registration attachmentType", attachmentType);
      console.log("Fido2Registration userId", userId);
      const result = await Fido2.registerButtonClicked(userId, {
        authenticatorAttachment: attachmentType
      });
      console.log("Fido2Registration result", result);

      if (credentials.length === 0 && (policy === null || policy === undefined)) {
        registerPassKeyMfaPolicy();
      }

      console.log("Fido2Registration fetchCredentials");
      fetchCredentials(); // 목록 새로고침
    } catch (error) {
      // console.error("패스키 등록 실패:", error);
      showDialog(DialogEnum.DEFAULT, "실패", "패스키 등록에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- cross-platform 전용 등록 핸들러
  const handleRegisterCrossPlatform = () => {
    Fido2Registration('cross-platform');
  };

  // --- 데스크톱 전용 platform 등록 핸들러
  const handleRegisterPlatform = () => {
    Fido2Registration('platform');
  };

  // 패스키 삭제 핸들러
  const handleDelete = async (credentialId) => {
    showDialog(DialogEnum.CONFIRM, "패스키 삭제", "이 패스키를 정말 삭제하시겠습니까?", async () => {
      try {
        setIsLoading(true);
        await Fido2.deleteButtonClicked(credentialId); // credentialId를 인자로 전달하는 함수 필요
        showDialog(DialogEnum.SUCCESS, "성공", "패스키가 삭제되었습니다.");
        fetchCredentials(); // 목록 새로고침
      } catch (error) {
        showDialog(DialogEnum.DEFAULT, "실패", "패스키 삭제에 실패했습니다.");
      } finally {
        setIsLoading(false);
      }
    });
  };

  // 1개의 패스키 라도 등록 되면 패스키 정책 등록
  const registerPassKeyMfaPolicy = async () => {
    console.log("registerPassKeyMfaPolicy");
    try {
      const request = {
        factorType: 'FIDO2',
        factorPolicyName: 'FIDO2 Policy',
        unifiedProfileId: userId,
        status: false, // 기본적으로 활성화 상태로 등록
        factorPolicyMeta: {} // 필요한 경우 메타데이터 추가
      };

      const res = await axios.post(`${IAM_SERVER_MULTIFACTORPOLICY}/create`, request);
      if (res.data.data) {
        console.log("registerPassKeyMfaPolicy res data", res.data.data);
      } else {
        console.error("패스키 인증 정책 등록에 실패했습니다. : ", res.data.resultMessage);
      }
    } catch (error) {
      console.error("패스키 인증 정책 등록에 실패했습니다.", error);
    }
  };

  useEffect(() => {
    if (!isPassword) {
      setStep("verify");
    } else {
      setStep("manage");
    }
    // setStep("manage");
  }, []);


  return (
    <div className={styles.layer__popWrap}>
      <div className={`${styles.layer__popCont} ${styles.user}`}>
        <div className={`${styles.layer__popHeader} ${styles.detail}`}>                 {/* 모달 헤더 */}
          <h1 className={`innocode-typography--headline-md innocode-typography--bold ${styles.contentAreaTitleText}`}>
             패스키 및 보안 키
          </h1>
          <InnocodeButton
              theme={"opacity"}
              designType='assistive'
              enablePadding={false}
              iconOnly={true}
              icon={<svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18 17.94 6M18 18 6.06 6" />
              </svg>
              }
              onClick={onClose}
          /> 
        </div>
        <div className={styles.layer__popDetail}>
          <>
            {/* 1단계: 현재 비밀번호 확인 */}
            {step === "verify" && (
              <div className={styles.verifyFormContainer}> {/* 구글 스타일 폼 컨테이너 */}
                <h3 className={styles.verifyTitle}>계속하려면 먼저 본인임을 인증하세요.</h3>
                <div className={styles.passwordInputGroup}>
                  <label htmlFor="current-password" className={styles.passwordLabel}>비밀번호 입력</label>
                  <input
                    id="current-password"
                    className={styles.passwordInputField}
                    type={showPassword ? "text" : "password"}
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        verifyPassword();
                      }
                    }}
                    aria-label="현재 비밀번호 입력"
                  />
                </div>
                <div className={styles.showPasswordContainer}>
                  <input
                    type="checkbox"
                    id="show-password"
                    className={styles.showPasswordCheckbox}
                    checked={showPassword}
                    onChange={() => setShowPassword(!showPassword)}
                  />
                  <label htmlFor="show-password" className={styles.showPasswordLabel}>비밀번호 표시</label>
                </div>
                <div className={styles.verifyButtonRow}>
                  <InnocodeButton
                    size="md"
                    designType="primary" 
                    fullWidth={true}
                    className={styles.nextButton}
                    onClick={verifyPassword}
                  >다음</InnocodeButton>
                  {/* <button className={styles.nextButton} onClick={verifyPassword}>다음</button> */}
                </div>
              </div>
            )}


            {/* 2단계: OTP 최초 등록 후 코드 검증 */}
            {step === "manage" && (
              <>
                <div className={styles.content}>
                  <div className={styles.description}>
                    <p>패스키를 사용하면 지문, 얼굴, 화면 잠금 또는 보안 키만으로 계정에 안전하게 로그인할 수 있습니다. 나만 사용할 수 있도록 화면 잠금을 비공개로 유지하고 보안 키를 안전하게 보관하세요.</p>
                  </div>

                  <div className={styles.createButtonContainer}>
                    {/* 기본적으로 노출되는 cross-platform (USB 등) 버튼 */}
                    <button className={styles.createButton} onClick={handleRegisterCrossPlatform} disabled={isLoading}>
                      + 보안 키 사용
                    </button>

                    {/* 데스크톱 환경일 때만 추가로 노출되는 platform (내장) 버튼 */}
                    {/* {isDesktop && (
                      <button className={styles.createButton} onClick={handleRegisterPlatform} disabled={isLoading}>
                        + 이 기기 사용 (Windows Hello / Touch ID)
                      </button>
                    )} */}
                  </div>

                  <div className={styles.passkeyListSection}>
                    <h3>패스키</h3>
                    <p className={styles.subtitle}>기기에서 패스키를 만들거나 보안 키에서 패스키를 만들 수 있습니다.</p>

                    {isLoading ? <p>로딩 중...</p> : (
                      <ul className={styles.deviceList}>
                        {credentials.length > 0 ? credentials.map(cred => (
                          <li key={cred.credentialId} className={styles.deviceItem}>
                            {/* <span className={styles.deviceIcon}>{getDeviceIcon(cred.device)}</span> */}
                            <div className={styles.deviceInfo}>
                              <p className={styles.deviceName}>{cred.description || '보안 키'}</p>
                              <p className={styles.deviceMeta}>생성일:  <TimeAgo date={cred.registeredAt} /></p>
                              <p className={styles.deviceMeta}>최근 사용일: {cred.authenticatedAt ? <TimeAgo date={cred.authenticatedAt} /> : '아직 사용되지 않음'}</p>
                            </div>
                            <div className={styles.deviceActions}>
                              <button className={styles.actionButton} onClick={() => handleDelete(cred.credentialId)}>
                                <span className="material-symbols-outlined">delete</span>
                              </button>
                            </div>
                          </li>
                        )) : (
                          <p>등록된 패스키가 없습니다.</p>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        </div>
      </div>
    </div>
  );
}

export default PassKeyAuthModal;
